import { useEffect, useState } from 'react';
import { listTaskSignatureBypasses } from '../../services/api';
import type { TaskSignatureBypassResponse } from '../../services/api/types.gen';

/**
 * Forfeited clinician signatures for a task (ROG-3269).
 *
 * A bypass can be recorded against any task type — visits, orders, misc tasks
 * and communications all reach `pending_clinician_signature` and all go through
 * the same signing gate. Every panel that renders a signature summary therefore
 * needs this, or a bypassed chart shows a green "Signed by" pill with no hint
 * that the clinician signature was forfeited. The printed artifacts already
 * state the forfeiture; the screen must not disagree with the paper.
 *
 * `refreshToken` re-fetches when the caller knows something changed (e.g. after
 * a signature action).
 */
export function useTaskSignatureBypasses(
  taskId: string | null | undefined,
  refreshToken?: unknown
): TaskSignatureBypassResponse[] {
  const [bypasses, setBypasses] = useState<TaskSignatureBypassResponse[]>([]);

  useEffect(() => {
    if (!taskId) {
      setBypasses([]);
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        // The generated client does not throw on 4xx/5xx; a rejected request
        // comes back as `resp.error` with `resp.data` undefined. It can still
        // throw below that — a dropped connection, a serialization failure —
        // and without this catch that became an *unhandled rejection*, because
        // the call below is fire-and-forget. CI caught it as a failing shard
        // with every test passing.
        const resp = await listTaskSignatureBypasses({
          path: { task_id: taskId },
        });
        if (cancelled) return;
        if (resp.data) {
          setBypasses(resp.data);
        } else if (resp.error) {
          console.error('Failed to load signature bypasses', resp.error);
        }
      } catch (err) {
        if (cancelled) return;
        // Leaves `bypasses` empty, which reads as "no forfeiture recorded".
        // Safe in the direction that matters: the badge falls back to the
        // signature state rather than claiming a bypass that may not exist.
        console.error('Failed to load signature bypasses', err);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [taskId, refreshToken]);

  return bypasses;
}
